import { useMemo } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import FindingGuidance from '../../components/FindingGuidance.jsx';
import { AREA_LABELS, categoryOfFinding } from '../../lib/finding-category.js';
import {
  AREA_ORDER, SEVERITY_ORDER, SEVERITY_WORD, STATUS_ORDER, STATUS_WORD,
  areaOf, driftById, notRunReason, orderChecks, sinceLastCollection,
} from '../../lib/security-checks.js';
import { ControlRefs, SeverityWord, StatusWord } from './parts.jsx';

/**
 * All checks (Security, mockup 2): every check of the latest collection on ConsoleTable, severity first and then status.
 * Columns: Status (dot and word), Check (title, id, and for a check that did not run the permission or license it
 * needs), Area, Severity, Since last collection (from drift). Filters: Status with counts, Severity, Area, plus search.
 * The row expands to the remediation, the guidance, the control references and the evidence. Filter values and the
 * expanded rows are the page's, so Fix first and deep links can set them.
 */
export default function ChecksTable({ posture, filterValues, onFilterChange, expandedIds, onExpandedChange }) {
  const findings = useMemo(() => (Array.isArray(posture?.findings) ? posture.findings : []), [posture]);
  const rows = useMemo(() => orderChecks(findings), [findings]);
  const drift = useMemo(() => driftById(posture), [posture]);
  const count = (s) => rows.filter((f) => f.status === s).length;
  const areas = AREA_ORDER.filter((a) => rows.some((f) => categoryOfFinding(f.id) === a));

  const columns = [
    { id: 'status', header: 'Status', sortValue: (f) => STATUS_ORDER[f.status] ?? 4, cell: (f) => <StatusWord status={f.status} /> },
    {
      id: 'check',
      header: 'Check',
      sortValue: (f) => String(f.title ?? '').toLowerCase(),
      cell: (f) => (
        <div className="min-w-0">
          <div className="font-semibold text-ipc-ink">{f.title}</div>
          <div className="font-mono text-[12.5px] text-ipc-muted">{f.id}</div>
          {f.status === 'skip' ? <div className="mt-0.5 text-[13px] text-ipc-muted" data-not-run-reason="">{notRunReason(f.id)}</div> : null}
          {f.status !== 'skip' && detailLine(f) ? <div className="mt-0.5 text-[13px] text-ipc-muted">{detailLine(f)}</div> : null}
        </div>
      ),
    },
    { id: 'area', header: 'Area', hideBelow: 'md', sortValue: (f) => areaOf(f) ?? '', cell: (f) => areaOf(f) ?? '' },
    { id: 'severity', header: 'Severity', sortValue: (f) => SEVERITY_ORDER[f.severity] ?? 5, cell: (f) => <SeverityWord severity={f.severity} /> },
    {
      id: 'since',
      header: 'Since last collection',
      hideBelow: 'lg',
      sortValue: (f) => sinceLastCollection(drift.get(f.id)) || null,
      cell: (f) => sinceLastCollection(drift.get(f.id)),
    },
  ];
  const filters = [
    {
      id: 'status',
      label: 'Status',
      options: ['fail', 'warn', 'pass', 'skip'].filter((s) => count(s) > 0).map((s) => ({ value: s, label: `${STATUS_WORD[s]} ${count(s)}` })),
      test: (f, v) => f.status === v,
    },
    {
      id: 'severity',
      label: 'Severity',
      options: Object.keys(SEVERITY_ORDER).filter((s) => rows.some((f) => f.severity === s)).map((s) => ({ value: s, label: SEVERITY_WORD[s] })),
      test: (f, v) => f.severity === v,
    },
    ...(areas.length ? [{ id: 'area', label: 'Area', options: areas.map((a) => ({ value: a, label: AREA_LABELS[a] })), test: (f, v) => categoryOfFinding(f.id) === v }] : []),
  ];

  return (
    <ConsoleTable
      label="All checks"
      framed={false}
      columns={columns}
      rows={rows}
      getRowId={(f) => f.id}
      rowName={(f) => `${f.id} ${f.title}`}
      searchText={(f) => `${f.id} ${f.title} ${SEVERITY_WORD[f.severity] ?? ''} ${STATUS_WORD[f.status] ?? ''} ${areaOf(f) ?? ''}`}
      searchPlaceholder="Search checks"
      filters={filters}
      filterValues={filterValues}
      onFilterChange={onFilterChange}
      expandedIds={expandedIds}
      onExpandedChange={onExpandedChange}
      pageSize={15}
      renderExpanded={(f) => <CheckDetails finding={f} />}
      emptyState={{ title: 'No checks', body: 'The latest collection has no security checks.' }}
    />
  );
}

function CheckDetails({ finding }) {
  return (
    <div className="space-y-3">
      {finding.status === 'skip' ? (
        <p className="text-sm text-ipc-ink">
          This check did not run. {notRunReason(finding.id)}. Once that is in place, the next collection runs it.
        </p>
      ) : null}
      {finding.status !== 'pass' && finding.remediation ? (
        <div className="border-l-4 border-ipc-light bg-white px-3 py-2">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ipc-muted">Remediation</div>
          <p className="text-sm text-ipc-ink">{finding.remediation}</p>
        </div>
      ) : null}
      <FindingGuidance id={finding.id} />
      <ControlRefs refs={finding.controlRefs} />
      {finding.detail && Object.keys(finding.detail).length > 0 ? (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ipc-muted">Evidence</div>
          <pre className="whitespace-pre-wrap break-words rounded-md border border-ipc-border bg-white px-3 py-2 font-mono text-xs text-ipc-ink">{JSON.stringify(finding.detail, null, 2)}</pre>
        </div>
      ) : null}
    </div>
  );
}

/** The one-line evidence summary of a check ("42% (12 / 28)", "No policy found"), or null. Kept from the old table. */
export function detailLine(finding) {
  const d = finding?.detail;
  if (!d || typeof d !== 'object') return null;
  if (d.pct !== undefined && d.total !== undefined) return `${d.pct}% (${d[Object.keys(d).find((k) => k !== 'pct' && k !== 'total' && typeof d[k] === 'number')] ?? '?'} / ${d.total})`;
  if (d.pct !== undefined) return `${d.pct}%`;
  if (d.count !== undefined && d.total !== undefined) return `${d.count} / ${d.total}`;
  if (d.count !== undefined) return `${d.count} found`;
  if (d.current !== undefined && d.max !== undefined) return `${d.current} / ${d.max} (${d.pct}%)`;
  if (d.policyFound !== undefined) return d.policyFound ? 'Policy active' : 'No policy found';
  if (d.policies !== undefined && typeof d.policies === 'number') return `${d.policies} ${d.policies === 1 ? 'policy' : 'policies'}`;
  if (d.level !== undefined) return `Level: ${d.level}`;
  if (d.countries !== undefined) return `${d.countries} countries`;
  if (d.eligibleAssignments !== undefined) return `${d.eligibleAssignments} eligible assignments`;
  if (d.withCerts !== undefined) return `${d.withCerts} cert-based, ${d.passwordOnly ?? 0} password-only`;
  return null;
}
